import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios, { type AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../../SettingsDataGridSelectionRow";
import type { Rule } from "../../../global/types";
import GroupRulesColumns from "./columns/GroupRulesColumns";

/** Passed from `GroupWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface GroupNavState {
	name: string;
}

interface GroupRulesProps {
	state?: GroupNavState | null;
}

export default function GroupRules({ state: stateFromBoundary }: GroupRulesProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groupRules");
	const navigate = useNavigate();
	const { groupId } = useParams<{ groupId: string }>();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: GroupNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the group. */
	const state = (stateFromBoundary ?? stateFromLocation) as GroupNavState;

	const [addRulesIsToggled, setAddRulesIsToggled] = useState(false);
	const [availableRules, setAvailableRules] = useState<any[]>([]);
	const [selectedRules, setSelectedRules] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [rulesToAdd, setRulesToAdd] = useState<any[]>([]);
	const [groupRules, setGroupRules] = useState<Rule[]>([]);
	const [editGroupIsToggled, setEditGroupIsToggled] = useState(false);
	const [deleteGroupIsToggled, setDeleteGroupIsToggled] = useState(false);
	const [removeRulesIsToggled, setRemoveRulesIsToggled] = useState(false);
	const [groupNameInput, setGroupNameInput] = useState("");
	const [groupNameError, setGroupNameError] = useState("");
	const [loading, setLoading] = useState(true);
	const [optionsLoading, setOptionsLoading] = useState(true);
	
	const init = useCallback(() => {
		const ruleQueryObject = {
			groupId: groupId,
		};
		axios.get(apiRoutes.getRules, {
			headers: authHeader,
			params: ruleQueryObject,
		})
			.then((response) => {
				setGroupRules(response.data.rules);
				setLoading(false);
			})
			.catch((error: AxiosError) => {
				console.log(error);
			});
	}, [authHeader, groupId]);
	
	const handleRuleStatusToggle = useCallback((ruleId: string, currentStatus: boolean) => {
		axios.put(apiRoutes.toggleRuleStatus(ruleId), {}, {
			headers: authHeader
		})
			.then(() => {
				init();
			})
			.catch((err: AxiosError) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, currentStatus ? messages.RULE_DEACTIVATION_SUCCESS_MSG : messages.RULE_ACTIVATION_SUCCESS_MSG);
			});
	}, [authHeader, init, handleToastAlert]);
	
	const removeRuleFromGroup = useCallback((ruleId: string) => {
		axios.delete(apiRoutes.removeRuleFromGroup(groupId as string, ruleId), { headers: authHeader })
			.then(() => {
				init();
			})
			.catch((error: AxiosError) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, messages.RULE_REMOVED_ERROR_MSG);
			});
	}, [authHeader, groupId, init, handleToastAlert]);

	// Stable column defs — new arrays on each render make MUI X discard autosized widths.
	const columns = useMemo(() => {
		return GroupRulesColumns({
			handleRuleStatusToggle,
			removeRuleFromGroup,
			navigate
		});
	}, []);

	const handleToggleGroupEditDialog = () => {
		setEditGroupIsToggled(!editGroupIsToggled);
		setGroupNameError("");
		setGroupNameInput("");
	};

	const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setGroupNameInput(e.target.value);
		setGroupNameError("");
	};

	const handleGroupNameChangeSubmit = () => {
		if (!groupNameInput) return;

		if (groupNameInput === state.name) {
			handleToggleGroupEditDialog();
			return;
		}

		axios.put(apiRoutes.updateGroup(groupId as string), { name: groupNameInput }, { headers: authHeader })
			.then(() => {
				setEditGroupIsToggled(false);
				updateCoAppNavigation(false, navigationItemReferences.groupNavItemIndex, groupNameInput, pathname);
			})
			.catch((e: AxiosError) => {
				console.error(e);
				if (e.response?.data && typeof e.response.data === "object" && "message" in e.response.data && 
					typeof e.response.data.message === "string" && e.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToggleGroupEditDialog();
				handleToastAlert(messageLevels.ERROR, messages.GROUP_NAME_UPDATE_ERROR_MSG);
			});
	};

	const handleToggleDeleteGroupDialog = () => {
		setDeleteGroupIsToggled(!deleteGroupIsToggled);
	};

	const confirmGroupDeletion = () => {
		axios.delete(apiRoutes.deleteGroup + "/" + groupId, {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.groupNavItemIndex);
			navigate(pages.groupManagement, { replace: true });
			handleToggleDeleteGroupDialog();
		}).catch((err: AxiosError) => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
			handleToggleDeleteGroupDialog();
		});
	};

	const addRulesToGroup = () => {
		const requestJson = {
			ruleIds: rulesToAdd.map((rule) => rule.id)
		};
		axios.post(apiRoutes.addRulesToGroup(groupId as string), requestJson, { headers: authHeader })
			.then(() => {
				init();
				toggleAddNewDialog();
			})
			.catch((error: AxiosError) => {
				console.error(error);
				toggleAddNewDialog();
				handleToastAlert(messageLevels.ERROR, messages.RULE_ADDED_ERROR_MSG);
			});
	};

	const addRuleToRulesToAdd = (value: any[]) => {
		//value is an array of rule names, we need to convert them to rule objects to display name properly
		value = value.map((rule: any) => {
			const tempArr = [...groupRules, ...availableRules];
			const foundRule = tempArr.find((row) => row.name === rule);
			return foundRule;
		});
		setRulesToAdd(value);
	};

	const initAvailableRules = () => {
		axios.get(apiRoutes.getRules, { headers: authHeader })
			.then((response) => {
				const availableRules = response.data.rules.filter((rule: Rule) => {
					const ruleIndex = groupRules.findIndex((groupRule) => groupRule?.id === rule?.id);
					return ruleIndex === -1;
				});
				setAvailableRules(availableRules);
				setOptionsLoading(false);
			})
			.catch((error: AxiosError) => {
				console.error(error);
			});
	};

	const bulkRemoveRulesFromGroup = () => {
		const ruleIds = gridRowSelectionIdsArray(selectedRules, groupRules);
		const deleteData = { ruleIds };
		const plural = ruleIds.length > 1;

		axios.delete(apiRoutes.bulkRemoveRulesFromGroup(groupId as string), { headers: authHeader, data: deleteData })
			.then(() => {
				init();
				setRemoveRulesIsToggled(false);
				setSelectedRules(emptyGridRowSelectionModel());
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.RULE_REMOVED_ERROR_MSG);
			});
	};

	const toggleAddNewDialog = () => {
		if (!addRulesIsToggled) {
			initAvailableRules();
			setAddRulesIsToggled(true);
		} else {
			setAddRulesIsToggled(false);
			setAvailableRules([]);
			setRulesToAdd([]);
			setOptionsLoading(true);
		}
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [state.name]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(groupRules, loading);
	const selectedRuleCount = resolveGridSelectedRowIds(selectedRules, groupRules).length;

	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} Rule Management`}
				subResourceType="Rule"
				resourceType="Group"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				addOnClickHandler={toggleAddNewDialog}
				addIsPresent={true}
				editIsPresent={true}
				editOnClickHandler={handleToggleGroupEditDialog}
				deleteIsPresent={true}
				deleteOnClickHandler={handleToggleDeleteGroupDialog}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={groupRules}
				pinnedColumns={["actions"]}
				allowSelection={true}
				targetResource="rules"
				parentResource="group"
				parentResourceName={state.name}
				selectionModel={selectedRules}
				setSelectionModel={setSelectedRules}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="rule"
						visibleCount={visibleRowCount}
						selectedCount={selectedRuleCount}
						loading={loading}
						onClearSelection={() => setSelectedRules(emptyGridRowSelectionModel())}
						actions={[{
							label: "Remove",
							icon: <Delete sx={destructiveIconSx} />,
							onClick: () => setRemoveRulesIsToggled(true),
							variant: "destructive",
						}]}
					/>
				}
			/>
			<CoAppAddItemsToListModal
				addButtonTitle="Save"
				inputLabel="Rule(s)"
				addItemsIsToggled={addRulesIsToggled}
				addItemsToCollection={addRulesToGroup}
				addItemsToNewArray={addRuleToRulesToAdd}
				dialogTitle={`Add rule(s) to ${state.name} group`}
				items={availableRules}
				itemsToAdd={rulesToAdd}
				toggleDialog={toggleAddNewDialog}
				loading={optionsLoading}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteGroupIsToggled}
				dialogTitle={`Delete ${state.name} group?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "group")}
				confirmClickHandler={confirmGroupDeletion}
				cancelClickHandler={handleToggleDeleteGroupDialog}
				actionText="Delete"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={removeRulesIsToggled}
				dialogTitle={`Remove ${gridRowSelectionSize(selectedRules, groupRules) > 1 ? "rules" : "rule"} from ${state.name} group?`}
				dialogMessage={`Are you sure that you want to remove the selected ${gridRowSelectionSize(selectedRules, groupRules) > 1 ? "rules" : "rule"} from the ${state.name} group?`}
				confirmClickHandler={bulkRemoveRulesFromGroup}
				cancelClickHandler={() => setRemoveRulesIsToggled(false)}
				actionText="Remove"
			/>
			<CoAppEditNameModal
				dialogOpen={editGroupIsToggled}
				dialogTitle="Edit group name"
				changeHandler={handleGroupNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleGroupNameChangeSubmit}
				cancelClickHandler={handleToggleGroupEditDialog}
				actionText="Save"
				editNameError={groupNameError}
				setEditNameError={setGroupNameError}
			/>
		</>
	);
}